import { useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector as useSelector } from "../../../../store";
import Chevron from "../../../UI/Icons/Chevron";
import Search from "../../../UI/Icons/Search";

const PositionsFilter = ({
  selectedPositionIds,
  setSelectedPositionIds,
}: {
  selectedPositionIds: number[];
  setSelectedPositionIds: (ids: number[]) => void;
}) => {
  const allPositionsList = useSelector((state) => state.vacancy.positions);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectAllRef = useRef<HTMLInputElement | null>(null);

  const filteredPositions = allPositionsList.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const allIds = useMemo(
    () => allPositionsList.map((c) => c.id),
    [allPositionsList]
  );
  const isAllSelected = selectedPositionIds.length === allIds.length;
  const isIndeterminate = selectedPositionIds.length > 0 && !isAllSelected;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keep indeterminate state in sync
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const togglePosition = (id: number) => {
    const exists = selectedPositionIds.includes(id);
    const newSelected = exists
      ? selectedPositionIds.filter((i) => i !== id)
      : [...selectedPositionIds, id];
    setSelectedPositionIds(newSelected);
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedPositionIds([]);
    } else {
      setSelectedPositionIds(allIds);
    }
  };

  const getLabel = () => {
    if (isAllSelected) return "All";
    if (selectedPositionIds.length === 0) return "All";
    return `${selectedPositionIds.length} Selected`;
  };

  return (
    <>
      {allPositionsList.length > 1 && (
        <>
          {/* Toggle Dropdown */}
          <div
            className="d-flex align-items-center gap-3 cursor-pointer"
            onClick={() => setOpenDropdown((prev) => !prev)}
          >
            <div className="fs-13 fw-700 text-black">{getLabel()}</div>
            <Chevron
              size={16}
              isOpen={openDropdown}
              // fill={openDropdown ? "black" : "#AAA"}
            />
          </div>

          {openDropdown && (
            <div
              className="position-absolute top-100 end-0 z-10 bg-teal-pale border border-black shadow-md w-266px rounded z-1"
              ref={dropdownRef}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Search */}
              <div className=" p-6px">
                <div className="form-group w-100 input-icon icon-left">
                  <Search />
                  <input
                    type="text"
                    className="form-control fs-16 fw-400"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              {/* Select All */}
              <div
                className="px-15px py-8px d-flex align-items-center hover-bg-off-white"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.preventDefault();
                  handleSelectAll();
                }}
              >
                <div className="theme-check">
                  <input
                    type="checkbox"
                    className="theme-check-input"
                    id="selectAll"
                    readOnly
                    checked={isAllSelected}
                    ref={selectAllRef}
                  />
                  <label
                    htmlFor="selectAll"
                    className="theme-check-label lh-24px fw-700 fs-16"
                  >
                    Select/Deselect All
                  </label>
                </div>
              </div>

              {/* List */}
              <div className="max-h-310px overflow-auto">
                {filteredPositions.length > 0 ? (
                  filteredPositions.map((position) => (
                    <div
                      key={position.id}
                      className="border-top border-black px-15px py-8px d-flex align-items-center hover-bg-off-white text-break"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.preventDefault();
                        togglePosition(position.id);
                      }}
                    >
                      <div className="theme-check">
                        <input
                          type="checkbox"
                          className="theme-check-input"
                          id={`candidate-${position.id}`}
                          readOnly
                          checked={selectedPositionIds.includes(position.id)}
                        />
                        <label
                          htmlFor={`candidate-${position.id}`}
                          className="theme-check-label lh-24px fs-16 fw-400"
                        >
                          {position.name}
                        </label>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-grey-dark py-2">
                    No candidates found
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default PositionsFilter;
